import { type ComponentType, lazy } from 'react';

import { CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  CalendarPopover,
  type SmartDateTimeFieldCalendarRenderProps,
  type SmartDateTimeFieldRender,
} from '@shared/dream-components/smartDateTimeField';
import cn from 'classnames';

import { POPOVER_PANEL_CLASSES } from '../ui/_styles/popover';
import { IconButton } from '../ui/IconButton';
import { PillTextInput } from '../ui/PillTextInput';

// The calendar loads when its popover first opens, so the routes don't carry it. A failed load leaves the time field,
// since a throw would unmount the composer around the picker.
const Calendar = lazy(
  (): Promise<{ default: ComponentType<SmartDateTimeFieldCalendarRenderProps> }> =>
    import('../ui/Calendar').then((module) => ({ default: module.Calendar })).catch(() => ({ default: () => null })),
);

const errorClassName = 'px-4 text-[13px] leading-4 text-red-500';

const focusClassName = 'focus:!border-[color:var(--community-primary-on-light,#000)] focus:!bg-white';

/** The community's pieces for the schedule picker's date and time field. `fieldLabel` names the date field. */
export const scheduleFieldRender = (fieldLabel: string): SmartDateTimeFieldRender => ({
  input: ({ inputProps, invalid, errorText, trailing, overlay, canClear }) =>
    trailing ? (
      <div className="flex flex-col gap-2">
        <div className="relative">
          <PillTextInput
            {...inputProps}
            aria-label={fieldLabel}
            aria-invalid={invalid}
            autoComplete="off"
            className={cn('truncate', focusClassName, { 'pr-20': canClear, 'pr-12': !canClear })}
          />
          {overlay ? (
            <div
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-y-0 left-px flex min-w-0 items-center overflow-hidden whitespace-nowrap pl-4',
                'text-[16px] font-medium tracking-[-0.13px] sm:text-[13px]',
                { 'right-20': canClear, 'right-12': !canClear },
              )}
            >
              {overlay}
            </div>
          ) : null}
          <div className="absolute inset-y-0 right-2 flex items-center">{trailing}</div>
        </div>
        {errorText ? <p className={errorClassName}>{errorText}</p> : null}
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        <PillTextInput {...inputProps} aria-invalid={invalid} className={focusClassName} />
        {errorText ? <p className={errorClassName}>{errorText}</p> : null}
      </div>
    ),
  iconButton: ({ icon, ...buttonProps }) => (
    <IconButton {...buttonProps} className="text-black/50 aria-expanded:bg-black/5 disabled:cursor-not-allowed">
      {icon === 'clear' ? (
        <XMarkIcon className="size-5" aria-hidden="true" />
      ) : (
        <CalendarIcon className="size-5" aria-hidden="true" />
      )}
    </IconButton>
  ),
  calendar: (calendarProps) => <Calendar {...calendarProps} />,
  panel: ({ children }) => <div className={cn(POPOVER_PANEL_CLASSES, 'flex flex-col gap-3 p-3')}>{children}</div>,
  popover: (popoverProps) => <CalendarPopover {...popoverProps} />,
});
